// StoreUptimeModal.js
// Modal that displays store-level gateway uptime timeline

import React, { useEffect, useState } from 'react';
import './StoreUptimeModal.css';

export default function StoreUptimeModal({ company, store, storeName, startDate, endDate, mode, onClose, onDayClick }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [healthData, setHealthData] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [gatewayTimelines, setGatewayTimelines] = useState([]); // For individual gateway timelines
  const [dailyGridData, setDailyGridData] = useState(null); // For gateway-by-day grid (rebate mode)
  const [hoveredBar, setHoveredBar] = useState(null);

  // Handler for clicking a day cell
  const handleDayClick = (gateway, day, uptimePercent) => {
    if (uptimePercent === null || !onDayClick) return;
    onDayClick({
      company,
      store,
      storeName,
      gateway: gateway.gwId,
      gatewayName: gateway.gwName,
      day,
      uptimePercent
    });
  };

  // Convert UTC timestamp string to local time string
  const utcToLocal = (utcTimeStr) => {
    // utcTimeStr format: "2026-01-08 17:00:00" (UTC)
    const utcDate = new Date(utcTimeStr.replace(' ', 'T') + 'Z');
    const localDate = new Date(utcDate.getTime());

    // Format as "YYYY-MM-DD HH:MM:SS" in local time
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    const seconds = String(localDate.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  useEffect(() => {
    if (!company || !store) return;

    async function fetchData() {
      try {
        setLoading(true);
        setError(null);

        // Use provided date range, or default to last 6 hours
        const endTime = endDate ? new Date(endDate) : new Date();
        const startTime = startDate ? new Date(startDate) : new Date(endTime.getTime() - 6 * 60 * 60 * 1000);

        const formatTime = (d) => d.toISOString().slice(0, 19).replace('T', ' ');

        // Analysis mode: Fetch individual gateway timelines
        // Rebate mode: Fetch daily grid data
        const endpoint = mode === 'analysis'
          ? `/api/gateway-uptime/store-gateways-timeline?company=${encodeURIComponent(company)}&store=${encodeURIComponent(store)}&startTime=${encodeURIComponent(formatTime(startTime))}&endTime=${encodeURIComponent(formatTime(endTime))}`
          : `/api/gateway-uptime/store-daily-grid?company=${encodeURIComponent(company)}&store=${encodeURIComponent(store)}&startTime=${encodeURIComponent(formatTime(startTime))}&endTime=${encodeURIComponent(formatTime(endTime))}`;

        const response = await fetch(endpoint, { credentials: 'include' });

        if (!response.ok) {
          throw new Error(`Failed to fetch uptime data: ${response.statusText}`);
        }

        const data = await response.json();

        if (mode === 'analysis') {
          // Individual gateway timelines
          console.log('[StoreUptimeModal] Received', data.gateways?.length || 0, 'gateway timelines');
          setGatewayTimelines(data.gateways || []);
        } else {
          // Daily grid for rebate mode
          console.log('[StoreUptimeModal] Received daily grid:', data.days?.length, 'days,', data.gateways?.length, 'gateways');
          setDailyGridData(data);
        }

      } catch (err) {
        console.error('Store uptime fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [company, store, startDate, endDate, mode]);

  // Render timeline bars
  function renderTimeline() {
    if (healthData.length === 0) {
      return <div style={{ padding: 20, textAlign: 'center', color: '#e0e0e0' }}>No uptime data available</div>;
    }

    return (
      <div className="timeline-container">
        <div className={`timeline-tooltip-fixed ${hoveredBar === null ? 'hidden' : ''}`}>
          {hoveredBar !== null && healthData[hoveredBar] ? (
            <>
              <div className="tooltip-time">{utcToLocal(healthData[hoveredBar].timeInterval)}</div>
              <div className={`tooltip-status ${healthData[hoveredBar].status}`}>
                {healthData[hoveredBar].status.toUpperCase()}
              </div>
              <div className="tooltip-count">Online: {healthData[hoveredBar].onlineCount} / {healthData[hoveredBar].totalCount}</div>
              <div className="tooltip-percent">{healthData[hoveredBar].uptimePercent.toFixed(1)}%</div>
            </>
          ) : (
            <>
              <div className="tooltip-time">--:--</div>
              <div className="tooltip-status">N/A</div>
              <div className="tooltip-count">Online: 0 / 0</div>
              <div className="tooltip-percent">0.0%</div>
            </>
          )}
        </div>
        <div className="timeline-bars" style={{ marginTop: '120px' }}>
          {healthData.map((item, idx) => {
            const uptimePercent = item.uptimePercent || 0;
            const height = Math.max(uptimePercent * 3, 30); // Scale height by uptime percentage
            const colorClass = uptimePercent >= 95 ? 'online' : uptimePercent >= 80 ? 'warning' : 'offline';

            return (
              <div
                key={idx}
                className={`timeline-bar ${colorClass} ${hoveredBar === idx ? 'hovered' : ''}`}
                style={{ height: `${height}px` }}
                onMouseEnter={() => setHoveredBar(idx)}
                onMouseLeave={() => setHoveredBar(null)}
                title={`${utcToLocal(item.timeInterval)} - ${uptimePercent.toFixed(1)}% uptime`}
              />
            );
          })}
        </div>
        <div className="timeline-labels">
          {healthData.filter((_, idx) => idx % Math.ceil(healthData.length / 10) === 0).map((item, idx) => (
            <span key={idx} className="timeline-label">
              {utcToLocal(item.timeInterval).slice(11, 16)}
            </span>
          ))}
        </div>
      </div>
    );
  }

  // Render individual gateway timelines (for Analysis mode)
  function renderGatewayTimelines() {
    if (gatewayTimelines.length === 0) {
      return <div style={{ padding: 20, textAlign: 'center', color: '#e0e0e0' }}>No gateway data available</div>;
    }

    return (
      <div className="individual-gateways-container">
        {gatewayTimelines.map((gw, gwIdx) => {
          // Remove company suffix from gateway ID
          const cleanGwId = gw.gwId.replace(/BPS$|WFC$|WLAB$|CDL$|AMZ$/i, '');

          if (!gw.timeline || gw.timeline.length === 0) {
            return (
              <div key={gwIdx} className="gateway-timeline-row">
                <div className="gateway-timeline-header">
                  <span className="gateway-id">{cleanGwId}</span>
                  <span className="gateway-uptime">No data</span>
                </div>
              </div>
            );
          }

          return (
            <div key={gwIdx} className="gateway-timeline-row">
              <div className="gateway-timeline-header">
                <span className="gateway-id">{cleanGwId}</span>
                <span className={`gateway-uptime ${gw.uptimePercent >= 95 ? 'good' : gw.uptimePercent >= 80 ? 'warning' : 'bad'}`}>
                  {gw.uptimePercent.toFixed(1)}%
                </span>
              </div>
              <div className="gateway-timeline-bars">
                {gw.timeline.map((interval, idx) => {
                  const colorClass = interval.status === 'online' ? 'online' : 'offline';
                  return (
                    <div
                      key={idx}
                      className={`gateway-bar ${colorClass}`}
                      title={`${utcToLocal(interval.timeInterval)} - ${interval.status}`}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Render daily grid for rebate mode (gateway-by-day)
  function renderDailyGrid() {
    if (!dailyGridData || dailyGridData.gateways.length === 0) {
      return <div style={{ padding: 20, textAlign: 'center', color: '#e0e0e0' }}>No uptime data available</div>;
    }

    const { days, gateways } = dailyGridData;

    // Format day headers (e.g., "Jan 1", "Jan 2")
    const formatDayHeader = (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Get color class based on uptime percentage
    const getUptimeClass = (percent) => {
      if (percent === null) return 'no-data';
      if (percent >= 99.1) return 'excellent';  // Green: 99.1% - 100%
      if (percent >= 95) return 'warning';      // Yellow: 95% - 99%
      return 'poor';                            // Red: < 95%
    };

    return (
      <div className="daily-grid-container">
        <div className="daily-grid-scroll">
          <table className="daily-grid-table">
            <thead>
              <tr>
                <th className="gateway-col sticky-col">Gateway</th>
                {days.map(day => (
                  <th key={day} className="day-col">
                    {formatDayHeader(day)}
                  </th>
                ))}
                <th className="overall-col">Overall</th>
              </tr>
            </thead>
            <tbody>
              {gateways.map((gateway, idx) => {
                // Remove company suffix from gateway ID
                const cleanGwId = gateway.gwId.replace(/BPS$|WFC$|WLAB$|CDL$|AMZ$/i, '');

                return (
                  <tr key={idx}>
                    <td className="gateway-col sticky-col" title={cleanGwId}>
                      <div className="gateway-name">{cleanGwId}</div>
                    </td>
                  {days.map(day => {
                    const uptime = gateway.dailyUptime[day];
                    const className = getUptimeClass(uptime);
                    return (
                      <td
                        key={day}
                        className={`day-cell ${className} ${uptime !== null ? 'clickable' : ''}`}
                        onClick={() => handleDayClick(gateway, day, uptime)}
                        title={uptime !== null ? `${uptime}% - Click to view ${gateway.gwName} on ${day}` : ''}
                      >
                        {uptime !== null ? `${uptime.toFixed(1)}%` : '-'}
                      </td>
                    );
                  })}
                  <td className={`overall-cell ${getUptimeClass(gateway.overallUptime)}`}>
                    <strong>{gateway.overallUptime.toFixed(1)}%</strong>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="grid-legend">
          <span className="legend-item"><span className="legend-dot excellent"></span> ≥95% (Excellent)</span>
          <span className="legend-item"><span className="legend-dot good"></span> 80-94% (Good)</span>
          <span className="legend-item"><span className="legend-dot warning"></span> 50-79% (Warning)</span>
          <span className="legend-item"><span className="legend-dot poor"></span> &lt;50% (Poor)</span>
          <span className="legend-item"><span className="legend-dot no-data"></span> No Data</span>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content store-uptime-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Store Uptime Timeline</h2>
          <div className="store-info">
            <span className="store-code">{store}</span>
            {storeName && <span className="store-name"> - {storeName}</span>}
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading store uptime data...</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p style={{ color: '#e74c3c' }}>Error: {error}</p>
            </div>
          )}

          {!loading && !error && (
            <>
              {mode === 'analysis' ? (
                // Analysis mode: Show individual gateway timelines
                <div className="timeline-section">
                  <h3>Individual Gateway Timelines{startDate || endDate ? '' : ' (Last 24 Hours)'}</h3>
                  {renderGatewayTimelines()}
                </div>
              ) : (
                // Rebate mode: Show daily grid (gateway-by-day)
                <div className="timeline-section">
                  <h3>Gateway Uptime by Day</h3>
                  <p className="grid-subtitle">Daily uptime percentages for rebate calculation</p>
                  {renderDailyGrid()}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
